import React, { useEffect, useState, useMemo, useRef } from 'react';
import superagent from 'superagent';
import {isPermitted, useFeathers} from '../../app/util';
import { useSelector } from 'react-redux';
import {
  useParams,
  useLocation,
  useHistory
} from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';

import {
  CoveragePlanMap,
	LoadingSpinner,
	SupportedDistrictList,
  DataDetailToolbar,
  TypeDropdown,
  DistrictSelectInput,
  EditBranchCoveragePlan,
  CoveragePlanSummaryCard
} from '../../components';
import TextField from '@mui/material/TextField';
import styled from 'styled-components';
import Typography from '@mui/material/Typography';
import qs from 'qs';
import { Button, CircularProgress, Tooltip, IconButton } from '@mui/material';

const POLL_INTERVAL_MS = 5 * 1000;
const MAX_DURATION_MS = 20 * 60 * 1000;

const ElectionCoveragePlan = () => {
  const feathers = useFeathers();
  const electionKey = useParams().key;
  const user = useSelector(state => state.user);
  const editPerms = isPermitted(user, ['editor', 'super-admin']);
  const electionDataPreloaded = useSelector(state => state?.elections?.byKey[electionKey]);
  const [ election, setElection ] = useState(electionDataPreloaded);
  const [ electionImplementation, setElectionImplementation ] = useState(null)
  const [ loading, setLoading ] = useState(!Boolean(electionDataPreloaded));
  const [ districtsCount, setDistrictsCount ] = useState(null)
  const [ editingPlan, setEditingPlan ] = useState(false)
  const [ configForState, setConfigForState ] = useState(null)
  const [ search, setSearch ] = useState('')
  const [ selectedCounty, setSelectedCounty ] = useState(null)
  const location = useLocation()
  const history = useHistory()
  const { status, districtType, eauType = 'eau', county } = qs.parse(location.search, { ignoreQueryPrefix: true })

  const onChangeStatusFilter = (newStatus) => {
    const newQuery = qs.stringify({ status: newStatus, districtType, eauType, county })
    const newLocation = { ...location, search: newQuery }
    history.push(newLocation)
  }

  const onChangeTypeFilter = (newType) => {
    const newQuery = qs.stringify({ status, districtType: newType, eauType, county })
    const newLocation = { ...location, search: newQuery }
    history.push(newLocation)
  }

  const onChangeEauTypeFilter = (newEauType) => {
    const newQuery = qs.stringify({ status, districtType, eauType: newEauType, county })
    const newLocation = { ...location, search: newQuery }
    history.push(newLocation)
  }

  const onChangeSearchTerm = (newSearch) => {
    setSearch(newSearch)
  }

  const onChangeCountyFilter = (newCounty) => {
    setSelectedCounty(newCounty)
    const newQuery = qs.stringify({
      status,
      districtType,
      eauType,
      ...(newCounty?._id ? { county: newCounty._id } : {}),
    })
    history.push({ ...location, search: newQuery })
  }

  const tier2TypeOptions = useMemo(() => {
    if(configForState) {
      return [].concat(
        [{ type: 'all', title: 'All districts' }],
        configForState.types.state.map(type => ({ type, title: type.split('-').map(t => t.slice(0,1).toUpperCase() + t.slice(1).toLowerCase()).join(' ') }))
      )
    }
    else return [
      { type: 'all', title: 'All districts' },
    ]
  }, [ configForState ])

  const refreshElectionData = async () => {
    try {
      const newData = await feathers.getService('elections').get(
        electionKey
      );
      setElection(newData)
      const eiData = await feathers.getService('election-implementations').get(electionKey);
      setElectionImplementation(eiData)

      const configForState = (
        await superagent.get(`https://districts-microservice.herokuapp.com/api/v1/district-configurations/${newData.stateCode.toLowerCase()}`)
      )
      setConfigForState(configForState.body)
      setLoading(false)
    } catch(err) {
      console.error(err)
    }
  }

  const refreshElectionImplementation = async () => {
    try {
      const eiData = await feathers.getService('election-implementations').get(electionKey);
      setElectionImplementation(eiData);
    } catch (err) {
      console.error(err);
    }
  }

  useEffect(() => {
    if(feathers) {
      refreshElectionData()
    }
  }, [ feathers ])

  useEffect(() => {
    if(!county) {
      setSelectedCounty(null)
      return
    }
    if(!feathers || `${selectedCounty?._id}` === `${county}`) return

    let cancelled = false
    feathers.getService('districts').get(county)
      .then(district => {
        if(!cancelled) setSelectedCounty(district)
      })
      .catch(err => console.error(err))

    return () => {
      cancelled = true
    }
  }, [ feathers, county, selectedCounty?._id ])

  const processInProgress = electionImplementation?.processInProgress;
  const pollingRef = useRef(null);

  useEffect(() => {
    if (!processInProgress || !feathers) return;

    const startedAt = processInProgress.startedAt ? new Date(processInProgress.startedAt).getTime() : Date.now();
    const elapsed = () => Date.now() - startedAt;

    if (elapsed() >= MAX_DURATION_MS) return;

    const poll = () => {
      if (elapsed() >= MAX_DURATION_MS) {
        if (pollingRef.current) clearInterval(pollingRef.current);
        pollingRef.current = null;
        return;
      }
      refreshElectionImplementation();
    };

    pollingRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [ processInProgress?.process, processInProgress?.startedAt, feathers ]);

  const processInProgressStatus = processInProgress?.process === 'refreshing-priority'
    ? 'Refreshing priorities…'
    : processInProgress?.process
      ? `Processing: ${processInProgress.process}`
      : 'Processing…';

  const listTitle = useMemo(() => {
    if(eauType === 'eau') return 'Scouting Districts';
    if(eauType === 'non-eau') return 'Non-EAU Districts';
    return 'Supported districts';
  }, [ eauType ])

  if(loading) {
    return (
      <Wrapper>
        <LoadingTile>
          <LoadingSpinner scale={0.8}/>
        </LoadingTile>
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <DataDetailToolbar
        onBack={{ to: {
            pathname: `/elections/${electionKey}`,
          }}}
        navTree={[
          {
            text: 'Elections',
            to: {
              pathname: `/elections`,
            }
          },
          {
            text: election?.name,
            to: {
							pathname: `/elections/${electionKey}`,
            }
          },
          { text: 'Coverage plan' }
        ]}
        actionButtonsComponent={
          <>
            {editingPlan && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => setEditingPlan(false)}
              >
                Cancel
              </Button>
            )}
            {processInProgress && (
              <Tooltip title={processInProgressStatus}>
                <CircularProgress size={28} sx={{ mr: 0.5 }} />
              </Tooltip>
            )}
          </>
        }
      />
      {
        editingPlan &&
        <EditBranchCoveragePlan
          style={{ marginTop: '24px', maxWidth: '600px' }}
          election={electionKey}
          onSave={(newElectionImplementation) => {
            setEditingPlan(false);
            setElectionImplementation(newElectionImplementation);
          }}
        />
      }
      {
				electionImplementation && !editingPlan &&
				<Inner>
					<CoveragePlanInfo>
						<Typography variant='h3'>Coverage plans</Typography>
						{[
							electionImplementation.branchCoverageArea && (
								<BranchCardRow key="branch">
									<CoveragePlanSummaryCard
										coverageArea={electionImplementation.branchCoverageArea}
										organizationKey={null}
                    electionKey={electionKey}
                    canEditProjectDate={editPerms}
									/>
									{editPerms && (
										<IconButton size="small" onClick={() => setEditingPlan(true)} title="Edit Branch coverage plan">
											<EditIcon />
										</IconButton>
									)}
								</BranchCardRow>
							),
							...(electionImplementation.organizationCoverageAreas || []).map((entry) => (
								<CoveragePlanSummaryCard
									key={entry.organizationKey}
									coverageArea={entry.coverageArea}
									secondaryCoverageArea={entry.secondaryCoverageArea}
									organizationKey={entry.organizationKey}
                  electionKey={electionKey}
                  canEditProjectDate={editPerms}
								/>
							))
						].filter(Boolean)}
					</CoveragePlanInfo>
					{
            Boolean(electionImplementation) && 
            <>
              <MapWrapper>
                <CoveragePlanMap
                  style={{ maxHeight: 'calc(500px - 36px * 2)' }}
                  electionImplementation={electionImplementation}
                />
              </MapWrapper>
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', marginBottom: '-8px', gap: '12px' }}>
                <Typography variant='h4'>{listTitle} ({typeof(districtsCount) === 'number' ? districtsCount : '--' })</Typography>
                <div style={{ display: 'flex', flexDirection: 'row', gap: '12px' }}>
                  <TextField
                    size="small"
                    placeholder="Search districts..."
                    value={search || ''}
                    onChange={(e) => onChangeSearchTerm(e.target.value)}
                    sx={{ minWidth: '200px' }}
                  />
                  <DistrictSelectInput
                    value={selectedCounty}
                    onChange={(e) => onChangeCountyFilter(e.target.value)}
                    descendantOf={election?.state?._id || election?.state}
                    districtTypes={['county']}
                    clearAfterSelect
                    placeholder="Filter by county"
                    style={{ minWidth: '220px' }}
                  />
                  <TypeDropdown
                    value={districtType || 'all'}
                    options={tier2TypeOptions}
                    onChange={onChangeTypeFilter}
                  />
                  <TypeDropdown
                    value={status || 'all'}
                    options={[
                      { type: 'incomplete', title: 'Incomplete' },
                      { type: 'complete', title: 'Completed' },
                      { type: 'all', title: 'All statuses' },
                    ]}
                    onChange={onChangeStatusFilter}
                  />
                  <TypeDropdown
                    value={eauType || 'eau'}
                    options={[
                      { type: 'eau', title: 'EAU Districts' },
                      { type: 'non-eau', title: 'Non-EAU Districts' },
                      { type: 'all', title: 'All Districts' },
                    ]}
                    onChange={onChangeEauTypeFilter}
                  />
                </div>
              </div>
              <ListWrapper>
                <SupportedDistrictList
                  onChangeCount={setDistrictsCount}
                  electionKey={electionKey}
                  statusFilter={status}
                  typeFilter={districtType}
                  eauTypeFilter={eauType || 'eau'}
                  countyFilter={county}
                  searchTerm={search}
                  onChangePageNumber={(pageNumberNew) => {
                    const newQuery = qs.stringify({ status, districtType, eauType: eauType || 'eau', county, page: pageNumberNew })
                    const newLocation = { ...location, search: newQuery }
                    history.push(newLocation)
                  }}
                  pageNumber={parseInt(qs.parse(location.search, { ignoreQueryPrefix: true }).page) || 1}
                />
              </ListWrapper>
            </>
          }
				</Inner>
			}
    </Wrapper>
  )
}


const Wrapper = styled.div`
  width: calc(100% - 2 * 24px);
  min-height: calc(100% - 24px * 2);
  display: flex;
  flex-direction: column;
  padding: 24px;
  align-items: stretch;
`;

const LoadingTile = styled.div`
  width: 100%;
  align-self: center;
  justify-self: center;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const MapWrapper = styled.div`
	max-width: calc(100% - 36px * 2);
  width: 600px;
	height: 400px;
	padding: 36px;
	background-color: #ffffff;
	border-radius: 8px;
	box-shadow: 0px 0px 20px 0px rgba(0,0,0,0.1);
`

const ListWrapper = styled.div`
	width: calc(100% - 24px * 2);
	height: auto;
	padding: 12px 24px 12px;
	background-color: #ffffff;
	border-radius: 8px;
	box-shadow: 0px 0px 20px 0px rgba(0,0,0,0.1);
`

const Inner = styled.div`
	display: flex;
	flex-direction: column;
	gap: 24px;
	padding-top: 36px;
`

const CoveragePlanInfo = styled.div`
	display: flex;
	flex-direction: column;
  align-items: stretch;
	gap: 12px;
  max-width: 600px
`

const BranchCardRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`

export default ElectionCoveragePlan;
